/**
 * Cart fetch interceptor
 *
 * Wraps window.fetch early so any Cart AJAX API mutation
 * (Nimstrata, upsell apps, gift forms, etc.) refreshes the Horizon
 * cart drawer + badge via `cart:update`.
 *
 * Theme requests that already pass `sections` are skipped — product-form
 * and cart-items-component dispatch their own cart events.
 */
(function () {
  'use strict';

  if (window.__hpCartFetchInterceptorInstalled) return;
  window.__hpCartFetchInterceptorInstalled = true;

  var originalFetch = window.fetch.bind(window);
  window.__hpOriginalFetch = originalFetch;

  var MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
  var debounceTimer = null;
  var DEBOUNCE_MS = 100;

  function getUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function getMethod(input, init) {
    if (init && init.method) return String(init.method).toUpperCase();
    if (typeof Request !== 'undefined' && input instanceof Request && input.method) {
      return String(input.method).toUpperCase();
    }
    return 'GET';
  }

  function getPathname(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
    } catch (error) {
      return '';
    }
  }

  function isCartMutation(url) {
    var pathname = getPathname(url).replace(/\.js$/, '');
    return MUTATION_PATHS.some(function (path) {
      return pathname === path || pathname.endsWith(path);
    });
  }

  /**
   * Only open for add-to-cart.
   * /cart/change and /cart/clear refresh silently — reopening after the shopper
   * closed the drawer mid-request felt broken.
   */
  function shouldOpenDrawer(url) {
    var pathname = getPathname(url).replace(/\.js$/, '');
    return pathname === '/cart/add' || pathname.endsWith('/cart/add');
  }

  function bodyHasSections(body) {
    if (!body) return false;

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return body.has('sections');
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return body.has('sections');
    }

    if (typeof body === 'string') {
      if (/(?:^|&)sections=/.test(body)) return true;
      try {
        var parsed = JSON.parse(body);
        return Boolean(parsed && parsed.sections);
      } catch (error) {
        return body.indexOf('"sections"') !== -1 || body.indexOf('sections=') !== -1;
      }
    }

    return false;
  }

  function requestHandledByTheme(input, init) {
    try {
      var url = new URL(getUrl(input), window.location.origin);
      if (url.searchParams.has('sections')) return true;
    } catch (error) {
      // ignore
    }

    if (init && bodyHasSections(init.body)) return true;

    if (typeof Request !== 'undefined' && input instanceof Request) {
      // Theme usually passes sections via init.body; Request clone read is async — skip.
    }

    return false;
  }

  function dispatchCartUpdate(cart, source, openDrawer) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: source,
          data: {
            itemCount: cart.item_count,
            source: source,
            openDrawer: openDrawer,
          },
        },
      })
    );
  }

  function openCartDrawer() {
    var drawer = document.getElementById('hp-cart-drawer');
    if (!drawer) return;

    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    if (typeof drawer.showDialog === 'function') {
      drawer.showDialog();
    }
  }

  function refreshFromCart(source, shouldOpen) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      originalFetch('/cart.js')
        .then(function (response) {
          if (!response.ok) throw new Error('cart.js ' + response.status);
          return response.json();
        })
        .then(function (cart) {
          dispatchCartUpdate(cart, source, shouldOpen);
          if (shouldOpen) openCartDrawer();
        })
        .catch(function (error) {
          console.error('[cart-fetch-interceptor]', error);
          if (shouldOpen) openCartDrawer();
        });
    }, DEBOUNCE_MS);
  }

  window.fetch = function (input, init) {
    var url = getUrl(input);
    var method = getMethod(input, init);
    var watch =
      method === 'POST' &&
      isCartMutation(url) &&
      !requestHandledByTheme(input, init);
    var shouldOpen = watch && shouldOpenDrawer(url);

    return originalFetch(input, init).then(function (response) {
      if (watch && response.ok) {
        refreshFromCart('cart-fetch-interceptor', shouldOpen);
      }
      return response;
    });
  };
})();
